import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Star, MessageSquarePlus } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function StarPicker({ value, onChange, size = "w-8 h-8" }) {
  const [hover, setHover] = useState(0);
  const shown = hover || value;
  return (
    <div className="flex gap-1" data-testid="star-picker">
      {[1, 2, 3, 4, 5].map((i) => (
        <button
          key={i}
          type="button"
          data-testid={`star-picker-${i}`}
          onMouseEnter={() => setHover(i)}
          onMouseLeave={() => setHover(0)}
          onClick={() => onChange(i)}
          className="transition-transform hover:scale-110"
          aria-label={`${i} stars`}
        >
          <Star
            className={`${size} ${i <= shown ? "fill-[#FFDF00] text-[#FFDF00]" : "text-zinc-300"}`}
            strokeWidth={1.5}
          />
        </button>
      ))}
    </div>
  );
}

export default function AddReviewDialog({ trigger, onSubmitted, initial = null }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", rating: 0, comment: "", email: "", ref_id: "" });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!initial) return;
    setForm((f) => ({ ...f, name: initial.name || "", rating: initial.rating || 0, ref_id: initial.ref_id || "" }));
    setOpen(true);
  }, [initial]);

  const submit = async (e) => {
    e.preventDefault();
    if (form.rating < 1) return toast.error("Pick a star rating");
    if (!form.name.trim()) return toast.error("Please add your name");
    setSubmitting(true);
    try {
      await axios.post(`${API}/reviews`, form);
      toast.success("Thanks — your review is live!", { icon: "⭐" });
      setForm({ name: "", rating: 0, comment: "", email: "", ref_id: "" });
      setOpen(false);
      onSubmitted?.();
      window.dispatchEvent(new CustomEvent("hp:reviews-changed"));
    } catch {
      toast.error("Could not submit review. Try again.");
    } finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <MessageSquarePlus className="w-5 h-5 text-[#0C2E2B]" /> Leave a Review
          </DialogTitle>
          <DialogDescription>
            {form.ref_id ? `Thanks for having us out (job ${form.ref_id}). Your review goes live right away.` : "Your review goes live on the site right away."}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4" data-testid="add-review-form">
          <div>
            <Label className="text-xs font-bold uppercase tracking-widest text-zinc-700">Your rating</Label>
            <div className="mt-2"><StarPicker value={form.rating} onChange={(v) => setForm({ ...form, rating: v })} /></div>
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-widest text-zinc-700">Name</Label>
            <Input data-testid="review-name-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Jane D." className="mt-1" />
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-widest text-zinc-700">Comment (optional)</Label>
            <Textarea data-testid="review-comment-input" rows={4} value={form.comment} onChange={(e) => setForm({ ...form, comment: e.target.value })} placeholder="Tell others what you thought of the crew..." className="mt-1" />
          </div>
          <Button data-testid="review-submit-btn" type="submit" disabled={submitting} className="btn-kinetic rounded-md w-full py-5">
            {submitting ? "Posting..." : "Post Review"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
